import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
// material
import { useTheme, styled, alpha } from '@mui/material/styles';
import {
  Box,
  List,
  Collapse,
  ListItemText,
  ListItemIcon,
  ListSubheader,
  ListItemButton,
  Tooltip,
  Stack
} from '@mui/material';
import { useToggleV2 } from 'src/hooks/useToggle';
import useCollapseDrawer from 'src/hooks/useCollapseDrawer';

// ----------------------------------------------------------------------
const ListSubheaderStyle = styled((props) => <ListSubheader disableSticky disableGutters {...props} />)(
  ({ theme }) => ({
    ...theme.typography.overline,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    color: theme.palette.primary.contrastText //alpha('#FFFFFF', 1)
  })
);

const ListItemStyle = styled(ListItemButton)(({ theme }) => ({
  ...theme.typography.body2,
  height: 37,
  fontSize: 16,
  // fontWeight: 'bold',
  position: 'relative',
  textTransform: 'none',
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  marginLeft: theme.spacing(1),
  marginRight: theme.spacing(1),
  color: '#000', //theme.palette.primary.contrastText, //alpha('#FFFFFF', 0.8),
  borderRadius: theme.shape.borderRadius,
  '&:hover': {
    color: '#2B3F7A' //theme.palette.primary.main
  }
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 23,
  height: 23,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

// ----------------------------------------------------------------------

NavItem.propTypes = {
  active: PropTypes.func,
  isShow: PropTypes.bool,
  item: PropTypes.object
};

function NavItem({ item, active, isShow, light, dark, isCollapse }) {
  const theme = useTheme();
  const isActiveRoot = active(item.path);
  const [hover, onHover, endHover] = useToggleV2();

  const { title, path, icon, info, children, fav } = item;

  const [open, setOpen] = useState(isActiveRoot);

  const handleOpen = () => {
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isActiveRoot) setOpen(false);
  }, [isActiveRoot]);

  const activeRootStyle = {
    color: '#2B3F7A', //'secondary.main',
    fontWeight: 'fontWeightMedium',
    bgcolor: '#E5F0FF', //alpha(theme.palette.secondary.main, 0.1),
    borderLeft: `4px solid ${theme.palette.secondary.main}`,
    '&:before': { display: 'block' }
  };

  const activeSubStyle = {
    color: 'white',
    fontWeight: 'fontWeightMedium'
  };

  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
          sx={{
            ...(isActiveRoot && activeRootStyle)
          }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>

          {isShow && (
            <>
              <ListItemText disableTypography primary={title} />
              {info && info}
              <Box
                component={Icon}
                icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
                sx={{ width: 16, height: 16, ml: 1 }}
              />
            </>
          )}
        </ListItemStyle>

        {isShow && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {children.map((item) => {
                const { title, path } = item;
                const isActiveSub = active(path);

                return (
                  <ListItemStyle
                    key={title}
                    component={RouterLink}
                    to={path}
                    sx={{
                      ...(isActiveSub && activeSubStyle)
                    }}
                  >
                    <ListItemIconStyle>
                      <Box
                        component="span"
                        sx={{
                          width: 4,
                          height: 4,
                          display: 'flex',
                          borderRadius: '50%',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'text.disabled',
                          transition: (theme) => theme.transitions.create('transform'),
                          ...(isActiveSub && {
                            transform: 'scale(2)',
                            bgcolor: 'primary.main'
                          })
                        }}
                      />
                    </ListItemIconStyle>
                    <ListItemText disableTypography primary={title} />
                  </ListItemStyle>
                );
              })}
            </List>
          </Collapse>
        )}
      </>
    );
  }

  if (fav) {
    return (
      <>
        <ListItemStyle
          component={RouterLink}
          onClick={handleOpen}
          to={path}
          sx={{
            ...(isActiveRoot && activeRootStyle)
          }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>

          {isShow && (
            <>
              <ListItemText disableTypography primary={title} />
              {info && info}
            </>
          )}
        </ListItemStyle>
        {isShow && isActiveRoot && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Stack pl={1}>{fav}</Stack>
          </Collapse>
        )}
      </>
    );
  }

  return (
    <ListItemStyle
      component={RouterLink}
      to={path}
      sx={{
        mb: 0.5,
        ...(isActiveRoot && activeRootStyle)
        // ...(light && { color: 'white', '&:hover': { color: 'white' } }),
        // ...(dark && { color: '#000', '&:hover': { color: '#000' } })
      }}
    >
      {isCollapse && hover ? (
        <Tooltip title={title} placement="right">
          <ListItemIconStyle onMouseLeave={endHover}>{icon && icon}</ListItemIconStyle>
        </Tooltip>
      ) : (
        <ListItemIconStyle onMouseEnter={onHover}>{icon && icon}</ListItemIconStyle>
      )}

      {(isShow || light || isCollapse) && (
        <>
          <ListItemText
            sx={{
              fontSize: 14
            }}
            primaryTypographyProps={{
              sx: {
                ...(dark && { color: '#000', '&:hover': { color: '#000' } })
              }
            }}
            disableTypography
            primary={title}
          />
          {info && info}
        </>
      )}
    </ListItemStyle>
  );
}

NavSection.propTypes = {
  isShow: PropTypes.bool,
  navConfig: PropTypes.array
};

export default function NavSection({ navConfig, isShow = true, dark, light = false, ...other }) {
  const { pathname } = useLocation();
  const { isCollapse } = useCollapseDrawer();

  const match = (path) => (path ? !!matchPath({ path, end: false }, pathname) : false);

  return (
    <Box {...other}>
      {navConfig.map((list, index) => {
        const { subheader, items } = list;
        return (
          <List key={subheader || index + '-sub'} disablePadding>
            {isShow && <ListSubheaderStyle>{subheader}</ListSubheaderStyle>}
            {items.map((item) => (
              <NavItem
                key={item.title}
                item={item}
                dark={dark}
                active={match}
                light={light}
                isShow={isShow}
                isCollapse={isCollapse}
              />
            ))}
          </List>
        );
      })}
    </Box>
  );
}
